import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'
import StatusToggle from '../ui/StatusToggle'
import ActionButtons from '../ui/ActionButtons'
import Modal from '../ui/Modal'
import SearchInput from '../ui/SearchInput'
import AddTopic from './AddTopic'
import EditTopic from './EditTopic'
import ConfirmModal from '../ui/ConfirmModal'

const PAGE_SIZE = 25

function TopicList() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editSubTopicId, setEditSubTopicId] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalAction, setStatusModalAction] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteSubTopicId, setDeleteSubTopicId] = useState(null)

  useEffect(() => {
    getTopics()
  }, [])

  const getTopics = async () => {
    setIsLoading(true)
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}getallactsubtopics`
    )
    if (response.status === 200) {
      setData(response.data)
    }
    setIsLoading(false)
  }

  // Filtered and paginated data
  const filteredTopics = data.filter(
    (topic) =>
      topic.topic_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.topic_code.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const totalPages = Math.ceil(filteredTopics.length / PAGE_SIZE) || 1
  const paginatedTopics = filteredTopics.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      <div className="flex justify-between items-center border px-5 py-4 border-[#F0F0F0]">
        <h2 className="text-lg text-[#030303] font-bold">
          ACT Sub Topics List
        </h2>
        <div className="flex items-center gap-2.5">
          <SearchInput
            placeholder="Search sub topic..."
            onSearch={setSearchQuery}
            className="w-[376px] max-md:w-[300px] max-sm:w-[200px]"
          />
          <button
            className="flex items-center gap-1 bg-[#DE3C7D] text-white p-2.5 rounded-md hover:bg-[#c8356f] transition-colors"
            onClick={() => setAddModalOpen(true)}
          >
            <span className="text-xs">Add Act Sub Topic</span>
            <i className="fa fa-plus"></i>
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DE3C7D]"></div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                  S.No
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                  Main Topic
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-40 px-5 py-3.5 border-[#F0F0F0]">
                  Topic Code
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-96 px-5 py-3.5 border-[#F0F0F0]">
                  Topic Name
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-40 px-5 py-3.5 border-[#F0F0F0]">
                  Type
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-32 px-5 py-3.5 border-[#F0F0F0]">
                  Status
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-32 px-5 py-3.5 border-[#F0F0F0]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedTopics.length > 0 ? (
                paginatedTopics.map((item, index) => (
                  <tr key={item._id} className="border border-[#F0F0F0]">
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      {(currentPage - 1) * PAGE_SIZE + index + 1}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.name}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.topic_code}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.topic_title}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.topic_type}
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      <StatusToggle
                        status={item.status}
                        onChange={() => {
                          setStatusModalAction({
                            id: item._id,
                            type: item.status ? 'block' : 'unblock',
                          })
                          setStatusModalOpen(true)
                        }}
                      />
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      <ActionButtons
                        onEdit={() => {
                          setEditSubTopicId(item._id)
                          setEditModalOpen(true)
                        }}
                        onDelete={() => {
                          setDeleteSubTopicId(item._id)
                          setDeleteModalOpen(true)
                        }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[#535862]">
                    No sub topics found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
      {filteredTopics.length > PAGE_SIZE && (
        <div className="flex justify-between items-center mt-4 px-4">
          <div className="text-sm text-[#535862]">
            Showing{' '}
            {filteredTopics.length === 0
              ? 0
              : (currentPage - 1) * PAGE_SIZE + 1}{' '}
            to {Math.min(currentPage * PAGE_SIZE, filteredTopics.length)} of{' '}
            {filteredTopics.length} entries
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`px-3 py-1 border border-[#F0F0F0] rounded-md ${
                  currentPage === index + 1 ? 'bg-[#DE3C7D] text-white' : ''
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
      {/* Add Sub Topic Modal */}
      {addModalOpen && (
        <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
          <AddTopic
            onClose={() => setAddModalOpen(false)}
            onSuccess={getTopics}
          />
        </Modal>
      )}
      {/* Edit Sub Topic Modal */}
      {editModalOpen && editSubTopicId && (
        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
          <EditTopic
            subTopicId={editSubTopicId}
            onClose={() => setEditModalOpen(false)}
            onSuccess={getTopics}
          />
        </Modal>
      )}
      {/* Status Change Confirmation Modal */}
      <ConfirmModal
        open={statusModalOpen}
        title={
          statusModalAction?.type === 'block'
            ? 'Deactivate Sub Topic'
            : 'Activate Sub Topic'
        }
        message={
          statusModalAction?.type === 'block'
            ? 'Are you sure you want to deactivate this sub topic?'
            : 'Are you sure you want to activate this sub topic?'
        }
        onCancel={() => {
          setStatusModalOpen(false)
          setStatusModalAction(null)
        }}
        onConfirm={async () => {
          if (statusModalAction) {
            if (statusModalAction.type === 'block') {
              await axios.put(
                `${process.env.REACT_APP_BASE_URL}block_actsubtopic/${statusModalAction.id}`
              )
              toast.success('Sub Topic deactivated')
            } else {
              await axios.put(
                `${process.env.REACT_APP_BASE_URL}unblock_actsubtopic/${statusModalAction.id}`
              )
              toast.success('Sub Topic activated')
            }
            await getTopics()
          }
          setStatusModalOpen(false)
          setStatusModalAction(null)
        }}
      />
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Sub Topic"
        message="Are you sure you want to delete this sub topic? This action cannot be undone."
        onCancel={() => {
          setDeleteModalOpen(false)
          setDeleteSubTopicId(null)
        }}
        onConfirm={async () => {
          if (deleteSubTopicId) {
            await axios.delete(
              `${process.env.REACT_APP_BASE_URL}delete_actsubtopic/${deleteSubTopicId}`
            )
            toast.success('Sub Topic deleted')
            await getTopics()
          }
          setDeleteModalOpen(false)
          setDeleteSubTopicId(null)
        }}
      />
    </section>
  )
}
export default TopicList
